import Route from '@ember/routing/route';
import type Transition from '@ember/routing/transition';
import { type Registry as Services, service } from '@ember/service';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import type { Transaction } from 'frontend-payments/models';
import type AjaxService from 'frontend-payments/services/ajax';
import type { ModelFrom } from 'frontend-payments/utils/routes';

type RemainingBalance = {
  amount: number;
  currency: string;
};

export type Model = ModelFrom<AuthenticatedRoute>;

export default class AuthenticatedRoute extends Route {
  queryParams = {
    redirectResult: {
      refreshModel: false,
    },
  };

  @service declare accountSwitcher: Services['account-switcher'];
  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  // Used for storing pending payment method state after 3DS redirect
  @service declare localStorage: Services['local-storage'];
  @service declare router: Services['router'];
  @service declare session: Services['session'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];
  @service declare usersnap: Services['usersnap'];

  handle3dsRedirectTask = task(
    {},
    async (redirectResult: string): Promise<void> => {
      try {
        const accountId = this.accountSwitcher.currentAccountId;

        if (!accountId) {
          throw new Error('Account ID is required for 3DS redirect');
        }

        const redirectResultDecoded = decodeURI(redirectResult);

        const paymentDetailsParsed = await this.ajax.post<{
          refusalReasonCode?: string;
        }>(`/api/v4/accounts/${accountId}/payments/details`, {
          contentType: 'application/json',
          data: {
            adyen_data: { details: { redirectResult: redirectResultDecoded } },
          },
        });

        // If the call didn't succeed, show an error message
        // e.g. card is detected as fraud, invalid card, ...
        if (paymentDetailsParsed.refusalReasonCode) {
          await this.alerting.showErrorAlert();
          return;
        }

        // Store pending payment method state in localStorage
        // The payment-details component will automatically detect this and show processing state
        this.storePendingPaymentMethod(accountId);

        // Show processing toast for recurring payment methods
        this.toast.success(
          this.intl.t(
            'payments.recurring_payment.recurring_payment_being_processed',
          ),
          {
            timeout: TOAST_TIMEOUT_LONG,
            showIcon: true,
          },
        );
      } catch (error) {
        this.errorTracking.captureException(error);
        await this.alerting.showErrorAlert();
      }
    },
  );

  queryAccountTask = task({}, async (accountId: string) => {
    try {
      const account =
        await this.accountSwitcher.loadCurrentAccountTask.perform(accountId);

      return account;
    } catch (error: unknown) {
      this.errorTracking.captureException(error);
      await this.alerting.showErrorAlert();
      throw new Error('Failed to fetch account');
    }
  });

  queryTransactionsTask = task({}, async (accountId: string) => {
    try {
      const apiResponse = await this.store.query<Transaction>('transaction', {
        adapterOptions: {
          namespace: `api/v4/accounts/${accountId}`,
        },
      });

      return {
        meta: apiResponse.meta as unknown as {
          remainingBalance: RemainingBalance;
        },
        transactions: [...apiResponse],
      };
    } catch (error: unknown) {
      this.errorTracking.captureException(error);
      await this.alerting.showErrorAlert();
      throw error;
    }
  });

  async beforeModel(transition: Transition): Promise<void> {
    this.session.requireAuthentication(transition, 'login');

    // Fetch user data first so we have email and can check if staff user
    try {
      await this.session.loadUserTask.perform();
    } catch (error: unknown) {
      this.errorTracking.captureException(error);
      await this.alerting.showErrorAlert();
      throw new Error('Failed to fetch user data');
    }

    // Set user context in Sentry once authenticated
    const email = this.session.user?.email;
    if (email) {
      this.errorTracking.setUser({ email });
    }

    if (this.session.user) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.usersnap.initialise(this.session.user);
    }

    // Setup alerting translations early
    this.alerting.setTranslations({
      showErrorAlert: {
        title: this.intl.t('general.error_title'),
        text: this.intl.t('general.error'),
      },
    });

    // Load available accounts early so currentAccountId is available
    try {
      await this.accountSwitcher.loadAccountsTask.perform();
    } catch (error: unknown) {
      this.errorTracking.captureException(error);
      await this.alerting.showErrorAlert();
      throw new Error('Failed to fetch available accounts');
    }

    // Check if user has any accounts available
    const accountId = this.accountSwitcher.currentAccountId;
    if (!accountId && !this.session.isStaffUser) {
      this.errorTracking.captureException(
        new Error('No accounts available for user'),
      );
      const contactConfirmed = await this.alerting.showErrorAlert({
        title: this.intl.t('payments.login.error.no_accounts'),
        text: this.intl.t('payments.login.error.no_accounts.description'),
        buttons: {
          cancel: {
            text: this.intl.t('general.cancel'),
          },
          confirm: { text: this.intl.t('payments.contact_support') },
        },
      });

      if (!contactConfirmed) {
        return;
      }

      const mailToLink = 'mailto:support@mylighthouse.com';
      window.open(mailToLink, '_self');
    }

    // Handle 3DS redirect if redirectResult query param is present
    const redirectResult = transition.to?.queryParams?.['redirectResult'] as
      | string
      | undefined;

    if (redirectResult) {
      await this.handle3dsRedirectTask.perform(redirectResult);

      this.router.transitionTo('authenticated.overview');
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  model() {
    const accountId = this.accountSwitcher.currentAccountId;

    if (!accountId) {
      throw new Error('Account ID is required to fetch transactions');
    }

    return {
      account: this.queryAccountTask.perform(accountId),
      availableAccounts: this.accountSwitcher.loadAccountsTask,
      transactionsRequest: this.queryTransactionsTask.perform(accountId),
    };
  }

  /**
   * Store pending payment method state in localStorage
   * Uses the same storage key format as payment-details component
   */
  storePendingPaymentMethod(accountId: string): void {
    const data = {
      timestamp: dayjs().valueOf(),
    };
    const storageKey = `pending-payment-method-${accountId}`;
    this.localStorage.setItem(storageKey, JSON.stringify(data));
  }
}
